import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

interface SiteSettings {
  id: string;
  site_name: string;
  description: string;
  update_interval: number;
  show_inactive_forums: boolean;
  min_score_threshold: number;
  default_sort_by: string;
  created_at: string;
  updated_at: string;
}

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    siteName: '',
    description: '',
    updateInterval: '24',
    showInactiveForums: true,
    minScoreThreshold: '1000',
    defaultSortBy: 'score',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .limit(1)
        .single();

      if (error) throw error;

      if (data) {
        setSettings({
          siteName: data.site_name || '',
          description: data.description || '',
          updateInterval: data.update_interval?.toString() || '24',
          showInactiveForums: data.show_inactive_forums || true,
          minScoreThreshold: data.min_score_threshold?.toString() || '1000',
          defaultSortBy: data.default_sort_by || 'score',
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Ayarlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { data: existingSettings, error: fetchError } = await supabase
        .from('site_settings')
        .select('id')
        .limit(1)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      const settingsData = {
        site_name: settings.siteName,
        description: settings.description,
        update_interval: parseInt(settings.updateInterval),
        show_inactive_forums: settings.showInactiveForums,
        min_score_threshold: parseInt(settings.minScoreThreshold),
        default_sort_by: settings.defaultSortBy,
      };

      let error;
      if (existingSettings?.id) {
        // Update existing settings
        const { error: updateError } = await supabase
          .from('site_settings')
          .update(settingsData)
          .eq('id', existingSettings.id);
        error = updateError;
      } else {
        // Insert new settings
        const { error: insertError } = await supabase
          .from('site_settings')
          .insert([settingsData]);
        error = insertError;
      }

      if (error) throw error;

      toast.success('Ayarlar kaydedildi. Zamanlanmış scrape: Supabase Cron → scheduled-scrape fonksiyonu.');
      
      // Refresh settings after save
      await fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Ayarlar kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Ayarlar</h2>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Site Adı
              </label>
              <input
                type="text"
                value={settings.siteName}
                onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Güncelleme Aralığı (Saat)
              </label>
              <input
                type="number"
                value={settings.updateInterval}
                onChange={(e) => setSettings({ ...settings, updateInterval: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Minimum Skor Eşiği
              </label>
              <input
                type="number"
                value={settings.minScoreThreshold}
                onChange={(e) => setSettings({ ...settings, minScoreThreshold: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Varsayılan Sıralama
              </label>
              <select
                value={settings.defaultSortBy}
                onChange={(e) => setSettings({ ...settings, defaultSortBy: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              >
                <option value="score">Skor</option>
                <option value="members">Üye Sayısı</option>
                <option value="posts">İleti Sayısı</option>
                <option value="topics">Konu Sayısı</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Site açıklaması
            </label>
            <textarea
              value={settings.description}
              onChange={(e) => setSettings({ ...settings, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="showInactiveForums"
              checked={settings.showInactiveForums}
              onChange={(e) => setSettings({ ...settings, showInactiveForums: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="showInactiveForums" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              İnaktif forumları göster
            </label>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <Save size={20} />
              {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;